import { matchPath } from 'react-router-dom';

/** The space a route belongs to (`/spaces/:spaceId/...`), or `undefined` on the Spaces home and system routes. */
export function spaceIdOf(pathname: string): string | undefined {
    const match = matchPath({ path: '/spaces/:spaceId/*', end: false }, pathname);
    const id = match?.params.spaceId;
    return id && id !== '' ? id : undefined;
}

/** The rail section a route belongs to. */
export type ShellSection = 'spaces' | 'overview' | 'pages' | 'changes' | 'settings' | undefined;

export function sectionOf(pathname: string): ShellSection {
    if (pathname === '/spaces' || pathname === '/spaces/') return 'spaces';
    const match = matchPath({ path: '/spaces/:spaceId/:section/*', end: false }, pathname);
    switch (match?.params.section) {
        case 'overview': return 'overview';
        case 'pages': return 'pages';
        case 'change-requests': return 'changes';
        case 'edit': return 'settings';
        default: return spaceIdOf(pathname) ? 'pages' : undefined;
    }
}

export interface RailModelEntry {
    key: Exclude<ShellSection, undefined>;
    label: string;
    to?: string;
    active: boolean;
    disabled?: boolean;
    bottom?: boolean;
    badge?: number;
    'aria-label'?: string;
}

/**
 * The rail (boards Ark-Spaces / Ark-Page / Ark-Settings). On the Spaces home: Spaces, then the space sections shown but
 * inert (no space open yet). Inside a space: Overview, Pages, Changes (proposed-count badge) and Settings at the bottom.
 */
export function railModel(pathname: string, proposedCount?: number): RailModelEntry[] {
    const spaceId = spaceIdOf(pathname);
    const section = sectionOf(pathname);
    const changesLabel = proposedCount ? `Change requests, ${proposedCount} proposed` : 'Change requests';
    if (!spaceId) {
        return [
            { key: 'spaces', label: 'Spaces', to: '/spaces', active: section === 'spaces' },
            { key: 'pages', label: 'Pages', active: false, disabled: true, 'aria-label': 'Pages (open a space first)' },
            { key: 'changes', label: 'Changes', active: false, disabled: true, 'aria-label': 'Change requests (open a space first)' },
            { key: 'settings', label: 'Settings', active: false, disabled: true, bottom: true, 'aria-label': 'Space settings (open a space first)' },
        ];
    }
    const base = `/spaces/${encodeURIComponent(spaceId)}`;
    return [
        { key: 'overview', label: 'Overview', to: `${base}/overview`, active: section === 'overview' },
        { key: 'pages', label: 'Pages', to: `${base}/pages`, active: section === 'pages' },
        { key: 'changes', label: 'Changes', to: `${base}/change-requests`, active: section === 'changes', badge: proposedCount, 'aria-label': changesLabel },
        { key: 'settings', label: 'Settings', to: `${base}/edit`, active: section === 'settings', bottom: true, 'aria-label': 'Space settings' },
    ];
}

/** `config.Environment` as the top bar's env pill (hidden in prod); anything that is not qa / prod is a laptop. */
export function topBarEnv(environment: string | undefined): 'qa' | 'prod' | 'local' {
    if (environment === 'prod') return 'prod';
    if (environment === 'qa') return 'qa';
    return 'local';
}
